import React, { useEffect, useRef } from 'react'
import * as Plot from '@observablehq/plot'
import PieChart from '@/components/PieChart/PieChart'

/**
 * Simple BarChart wrapper using Observable Plot
 */
export type BarChartProps<T extends object = any> = {
  data: T[]
  /** x field key */
  x: keyof T & string
  /** y field key */
  y: keyof T & string
  title?: string
  xLabel?: string
  yLabel?: string
  /** Force X domain and order (useful for categories like weekdays) */
  xDomain?: (string | number)[]
  /** Optional tick formatter for X axis */
  xTickFormat?: (d: any) => string
  width?: number
  height?: number
}

export function BarChart<T extends object = any>({
  data,
  x,
  y,
  title,
  xLabel,
  yLabel,
  xDomain,
  xTickFormat,
  width = 640,
  height = 360,
}: BarChartProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!data) return

    const plot = Plot.plot({
      marks: [
        Plot.ruleY([0]),
        Plot.barY(data as any[], {
          x,
          y,
        }),
      ],
      x: {
        label: xLabel,
        grid: true,
        // Force a band scale for bars (categorical/discrete), even if domain is numeric (e.g., 0–23 hours)
        type: 'band',
        domain: xDomain as any,
        tickFormat: xTickFormat,
      },
      y: {
        label: yLabel,
        grid: true,
      },
      marginLeft: 60,
      marginBottom: 50,
      width,
      height,
      title,
      style: {
        fontSize: '16px',
      },
    })

    if (containerRef.current) {
      containerRef.current.innerHTML = ''
      containerRef.current.append(plot)
    }

    return () => {
      plot.remove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data), x, y, title, xLabel, yLabel, JSON.stringify(xDomain), width, height])

  return <div ref={containerRef} />
}

/**
 * TimeSeriesChart for date vs value using Observable Plot
 */
export type TimeSeriesPoint = { date: Date; value: number }

export type TimeSeriesChartProps = {
  data: TimeSeriesPoint[]
  title?: string
  xLabel?: string
  yLabel?: string
  width?: number
  height?: number
  /** add dots on line */
  dots?: boolean
}

export function TimeSeriesChart({
  data,
  title,
  xLabel,
  yLabel,
  width = 800,
  height = 360,
  dots = true,
}: TimeSeriesChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!data) return

    const plot = Plot.plot({
      marks: [
        Plot.ruleY([0]),
        Plot.lineY(data as any[], { x: 'date', y: 'value', curve: 'catmull-rom' }),
        ...(dots ? [Plot.dot(data as any[], { x: 'date', y: 'value' })] : []),
      ],
      x: {
        label: xLabel,
        grid: true,
        type: 'utc',
      },
      y: {
        label: yLabel,
        grid: true,
      },
      marginLeft: 60,
      marginBottom: 50,
      width,
      height,
      title,
      style: {
        fontSize: '18px',
      },
    })

    if (containerRef.current) {
      containerRef.current.innerHTML = ''
      containerRef.current.append(plot)
    }

    return () => {
      plot.remove()
    }
  }, [JSON.stringify(data), title, xLabel, yLabel, width, height, dots])

  return <div ref={containerRef} />
}

export type ScatterChartPoint = { x: number; y: number }

export type ScatterChartProps = {
  data: ScatterChartPoint[]
  title?: string
  xLabel?: string
  yLabel?: string
  width?: number
  height?: number
  showTrendline?: boolean
}

export function ScatterChart({
  data,
  title,
  xLabel,
  yLabel,
  width = 720,
  height = 360,
  showTrendline = true,
}: ScatterChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!data || data.length === 0) return

    const marks: Plot.Mark[] = [Plot.dot(data as any[], { x: 'x', y: 'y' })]

    if (showTrendline && data.length >= 2) {
      const meanX = data.reduce((sum, p) => sum + p.x, 0) / data.length
      const meanY = data.reduce((sum, p) => sum + p.y, 0) / data.length
      let numerator = 0
      let denominator = 0
      for (const point of data) {
        const dx = point.x - meanX
        numerator += dx * (point.y - meanY)
        denominator += dx * dx
      }
      const slope = denominator === 0 ? 0 : numerator / denominator
      const intercept = meanY - slope * meanX
      const minX = Math.min(...data.map((p) => p.x))
      const maxX = Math.max(...data.map((p) => p.x))
      const linePoints = [
        { x: minX, y: slope * minX + intercept },
        { x: maxX, y: slope * maxX + intercept },
      ]
      marks.push(Plot.line(linePoints as any[], { x: 'x', y: 'y', stroke: '#555', strokeWidth: 2 }))
    }

    const plot = Plot.plot({
      marks,
      x: {
        label: xLabel,
        grid: true,
      },
      y: {
        label: yLabel,
        grid: true,
      },
      width,
      height,
      marginLeft: 60,
      marginBottom: 50,
      title,
      style: {
        fontSize: '16px',
      },
    })

    if (containerRef.current) {
      containerRef.current.innerHTML = ''
      containerRef.current.append(plot)
    }

    return () => {
      plot.remove()
    }
  }, [JSON.stringify(data), title, xLabel, yLabel, width, height, showTrendline])

  return <div ref={containerRef} />
}

export type LineChartPoint = { x: number; y: number }

export type LineChartProps = {
  data: LineChartPoint[]
  title?: string
  xLabel?: string
  yLabel?: string
  width?: number
  height?: number
  curve?: 'linear' | 'step'
}

export function LineChart({
  data,
  title,
  xLabel,
  yLabel,
  width = 720,
  height = 360,
  curve = 'linear',
}: LineChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!data || data.length === 0) return

    const curveType = curve === 'step' ? 'step-after' : undefined

    const plot = Plot.plot({
      marks: [
        Plot.ruleY([0]),
        Plot.lineY(data as any[], { x: 'x', y: 'y', curve: curveType }),
        Plot.dot(data as any[], { x: 'x', y: 'y' }),
      ],
      x: {
        label: xLabel,
        grid: true,
      },
      y: {
        label: yLabel,
        grid: true,
      },
      width,
      height,
      marginLeft: 60,
      marginBottom: 50,
      title,
      style: {
        fontSize: '16px',
      },
    })

    if (containerRef.current) {
      containerRef.current.innerHTML = ''
      containerRef.current.append(plot)
    }

    return () => {
      plot.remove()
    }
  }, [JSON.stringify(data), title, xLabel, yLabel, width, height, curve])

  return <div ref={containerRef} />
}

/**
 * PieChartWrapper using existing D3 PieChart utility
 */
export type PieChartDatum = { name: string; value: number }

export type PieChartWrapperProps = {
  data: PieChartDatum[]
  width?: number
  height?: number
  title?: string
}

export function PieChartWrapper({ data, width = 480, height = 320, title }: PieChartWrapperProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!data || data.length === 0) return

    const svg = PieChart<PieChartDatum>(data, {
      name: (d) => d.name,
      value: (d) => d.value,
      width,
      height,
      fontSize: 12,
    })

    if (containerRef.current) {
      containerRef.current.innerHTML = ''
      containerRef.current.append(svg)
    }

    return () => {
      svg.remove()
    }
  }, [data])

  return (
    <div>
      {title ? <h3 style={{ marginBottom: 8 }}>{title}</h3> : null}
      <div ref={containerRef} />
    </div>
  )
}

export default {
  BarChart,
  TimeSeriesChart,
  ScatterChart,
  LineChart,
  PieChartWrapper,
}
